import {
  Microscope,
  Lightbulb,
  TrendingUp,
  FileText,
  Brain,
  Activity,
  ShieldCheck,
  Heart,
  CheckCircle,
  Settings,
  PenTool,
  UserPlus,
  GraduationCap
} from "lucide-react"
 
export default function ResearchInnovations() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary text-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 uppercase tracking-wide">Research and Innovations</h1>
          <p className="text-lg md:text-xl text-white/90">Pioneering Healthcare Solutions</p>
        </div>
      </div>
 
      <div className="container mx-auto px-4 py-8 md:py-12">
       
        {/* Overview & Mission */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-10 mb-12 border-l-8 border-primary">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Overview</h2>
          <div className="prose prose-blue max-w-none text-gray-700 leading-relaxed space-y-6 text-lg">
            <p>
              The Endeavour of the Clinical research unit of Pranaam Hospital is to foster research to determine the safety and effectiveness (efficacy) of medications, devices and treatment regimens intended for Human use. Clinical research is different from clinical practices.
            </p>
            <p>
              In clinical practice established treatments are used, while in clinical research evidence is collected and established a treatment. Pranaam Hospital Pvt Ltd, encourages collaborative activities by the faculty, clinical departments and other units with industry as well as investigator-driven research so as to help in science and technology pertaining in Healthcare in turn making it possible to have the availability of diagnostic and therapeutic modalities so that our patients can lead to a better tomorrow.
            </p>
            <p>
              Pranaam Hospital has initiated research in areas such as Pediatrics, Gynaecology, General Medicine, Orthopedics, Endocrinology, Oncology and Vaccinations. Pranaam involved in undertaking various clinical and drug trials in its Clinical Research. The research department takes on clinical trials for Indian as well as international pharmaceutical companies.
            </p>
            <p>
              Research activities carried out in the hospital are in compliance with the New Drugs & Clinical Trials Rules 2019, and are monitored by a Pranaam Institutional Ethics Committee (IEC) registered with the Central Drugs Standard Control Organization (CDSCO).
            </p>
          </div>
        </div>
 
        {/* Quality & Team Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16 items-center">
          <div className="order-2 lg:order-1">
            <h2 className="text-3xl font-bold mb-6 text-primary">Clinical Research</h2>
            <div className="space-y-4 text-gray-600 text-lg">
              <p>
                Pranaam Clinical research is a comprehensive site management organization serving the biopharmaceutical and medical device industry. Our mission has been, and will always be quality.
              </p>
              <p>
                We strive to lead the world in the diagnosis and treatment of disease and to train tomorrow’s great doctors and nurses. Above all, we aim to provide the highest quality health-care services to all of our patients.
              </p>
              <p>
                Our clinical research department has well trained and experienced clinical research professionals focusing exclusively on conduct and drug development services to service the need of study investigators, the sponsors and the contract research organizations.
              </p>
            </div>
          </div>
          <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-lg h-[400px]">
            <img
              src="/images/Clinical_research.png"
              alt="Clinical Research"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = "/images/SingleRooms.png" }}
            />
          </div>
        </div>
 
        {/* Audits & Expertise */}
        <div className="bg-primary/5 rounded-2xl p-8 md:p-12 mb-16 border border-primary/10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-primary rounded-lg text-white">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold">Regulatory Audits & Global Standards</h3>
            </div>
            <div className="space-y-4 text-gray-700 text-lg">
              <p>
                Within our organization, we possess considerable expertise in managing Sponsor Audits conducted by esteemed companies like <span className="font-bold text-gray-900">BHARATH BIOTECH, CALL HEALTH, and TARO</span>.
              </p>
              <p>
                Moreover, we have effectively undergone regulatory audits conducted by the <span className="font-bold text-primary">Central Drugs Standard Control Organization (CDSCO)</span>, the primary regulatory authority in India.
              </p>
              <p>
                Our team is highly skilled in conforming to regulatory directives and ensuring alignment with global standards, establishing us as a reliable collaborator for clinical research initiatives. We celebrate successful trials and promote medical science by working with leaders in the industry and academic organizations.
              </p>
            </div>
          </div>
        </div>
 
        {/* Therapeutic Areas Grid */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Therapeutic Areas</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Leading the revolutionary path via clinical research across various medical specialties.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ResearchCard
              icon={<Microscope className="w-8 h-8" />}
              title="Oncology"
              desc="Researching cutting-edge cancer treatments, including targeted therapy, personalised medicine, and early diagnosis."
            />
            <ResearchCard
              icon={<Brain className="w-8 h-8" />}
              title="Neurology"
              desc="The study and treatment of neurological conditions such as multiple sclerosis, Parkinson's disease, and Alzheimer's."
            />
            <ResearchCard
              icon={<Activity className="w-8 h-8" />}
              title="Diabetes and Endocrinology"
              desc="Discovering developments in the treatment and avoidance of diabetes together with investigations into other hormonal problems."
            />
            <ResearchCard
              icon={<FileText className="w-8 h-8" />}
              title="Women's Health"
              desc="Examining advancements in prenatal care, reproductive health, and the unique health issues faced by women."
            />
            <ResearchCard
              icon={<ShieldCheck className="w-8 h-8" />}
              title="Pediatric Oncology"
              desc="Investigating novel treatments and customized strategies to combat pediatric malignancies."
            />
            <ResearchCard
              icon={<Heart className="w-8 h-8" />}
              title="Cardiology"
              desc="The study of novel therapies for stroke, heart disease, and other cardiovascular disorders."
            />
          </div>
        </div>
 
        {/* Pediatric Research Focus Section */}
        <div className="bg-white rounded-3xl shadow-xl border overflow-hidden mb-20">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 relative h-[300px] lg:h-auto">
              <img
                src="/images/PediatricResearch.png"
                alt="Pediatric Research"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2070" }}
              />
            </div>
            <div className="lg:col-span-3 p-8 md:p-12">
              <h2 className="text-3xl font-bold mb-6 text-primary">Pediatric Research Focus</h2>
              <p className="text-gray-700 mb-8 text-lg">
                Pranaam Hospitals proudly champions the future of healthcare, especially for our littlest patients. We believe in harnessing the power of clinical research to unlock life-changing advancements in pediatric medicine.
              </p>
              <div className="grid sm:grid-cols-2 gap-8">
                <PediatricFocusItem title="Pediatric Neurology" desc="Studying novel approaches to treating neurological conditions such as autism and epilepsy." />
                <PediatricFocusItem title="Immunology" desc="Creating vaccines against childhood illnesses that are both safer and more effective." />
                <PediatricFocusItem title="Genetics" desc="Solving the riddles of hereditary illnesses and possible gene therapy remedies." />
                <PediatricFocusItem title="Nutrition and Growth" desc="Researching the best nutritional and growth strategies for wholesome children." />
              </div>
            </div>
          </div>
        </div>
 
        {/* Clinical Research Services */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Clinical Research Services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ServiceCard
              icon={<Settings className="w-8 h-8" />}
              title="Study Design & Protocol"
              desc="Experience choosing right outcome measures, creating protocols, and planning research studies."
            />
            <ServiceCard
              icon={<PenTool className="w-8 h-8" />}
              title="Writing Research Grants"
              desc="Creating study proposals for community interventions and submitting grant applications."
            />
            <ServiceCard
              icon={<UserPlus className="w-8 h-8" />}
              title="Recruitment & Enrollment"
              desc="Committed team helping with recruitment and enrollment of eligible diverse individuals."
            />
            <ServiceCard
              icon={<GraduationCap className="w-8 h-8" />}
              title="Internship Opportunities"
              desc="Professional training opportunities at the clinical research department's clinical trial unit."
            />
          </div>
        </div>
 
      </div>
    </div>
  )
}
 
function ResearchCard({ icon, title, desc }) {
  return (
    <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all group">
      <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-primary group-hover:bg-primary group-hover:text-white transition-all">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  )
}
 
function PediatricFocusItem({ title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="mt-1 flex-shrink-0">
        <CheckCircle className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
        <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}
 
function ServiceCard({ icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 text-center hover:shadow-md transition-shadow border border-gray-50 flex flex-col items-center">
      <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4 text-primary">
        {icon}
      </div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
    </div>
  )
}
 